import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

// ── 型定義（BE: docs/api/library.md に基づく） ────────────────────────

/** セクション権限設定で選択できるプロジェクト内ポジション（API 表記） */
export type LibraryPosition =
  | "CLIENT"
  | "REVIEWER"
  | "PRODUCER"
  | "DIRECTOR"
  | "SPECIALIST";

/**
 * セクション内の実効ロール。
 * - viewer = ロール無しだが閲覧可（対象プロジェクトのメンバー）
 * - editor = ファイル/フォルダ/共有リンクの編集可
 * - owner = + ロール設定の変更可
 */
export type LibraryRole = "viewer" | "editor" | "owner";

/** ロール設定で割り当てられるロール */
export type LibraryRoleName = "owner" | "editor";

/** 作成者の表示用サマリー */
export interface LibraryUserBrief {
  id: number;
  display_name: string;
  avatar: string | null;
}

/** ロール割り当て 1 件（position / user はどちらか一方） */
export interface LibraryRoleEntry {
  id: number;
  role: LibraryRoleName;
  position: LibraryPosition | null;
  user: LibraryUserBrief | null;
}

/** セクションの権限設定（master_admin / セクションオーナーに返る） */
export interface LibrarySectionSettings {
  projects: { id: string; name: string }[];
  allowed_positions: LibraryPosition[];
  /** false = プロジェクト進行中のみアクセス可 */
  allow_completed: boolean;
  /** ロール設定（オーナー / エディター） */
  roles: LibraryRoleEntry[];
}

/** ライブラリのファイル種別（トークルーム添付と同じ分類） */
export type LibraryFileType =
  | "image"
  | "video"
  | "audio"
  | "document"
  | "other";

/**
 * ライブラリのファイル。ツリー API には含まれず、ファイル取得 API
 * （getLibrarySectionFiles / 検索 / ブックマーク一覧の files）が返す共通形。
 */
export interface LibraryFileItem {
  id: string;
  file_key: string | null;
  original_url: string | null;
  /** MediaConvert 変換済みメディアの URL（audio: m4a / video: HLS 等） */
  optimized_url: string | null;
  /** Lambda 生成の波形 peaks JSON（20 点/秒）の URL */
  waveform_url: string | null;
  /** 波形 peaks JSON が生成済みか */
  has_waveform: boolean;
  /** Lambda 生成のサムネイル（動画フレーム / PDF 1 ページ目） */
  thumbnail: string | null;
  file_name: string;
  file_type: LibraryFileType;
  /** サイズ情報の JSON 文字列（{"size": bytes, "size_converted": "..."}） */
  file_metadata: string | null;
  /** 所属フォルダ（null = ルート直下） */
  folder_id: string | null;
  /** 有効な共有リンクが存在するか */
  is_shared: boolean;
  /** オーディオブックマークの件数 */
  bookmark_count: number;
  /** ブックマーク作成者（重複なし・作成順。一覧のアバター表示用） */
  bookmark_users: LibraryUserBrief[];
  created_by: LibraryUserBrief | null;
  created_at: string;
  updated_at: string;
}

/** ツリーに含まれるフォルダ */
export interface LibraryFolderItem {
  id: string;
  name: string;
  /** 親フォルダ（null = ルート直下） */
  parent_id: string | null;
  /** 有効な共有リンクが存在するか */
  is_shared: boolean;
  updated_at: string;
}

/**
 * フォルダ直下のファイル集計（ツリー API の folder_stats）。
 * ファイルが 1 件も無いフォルダの行は無い。配下（子孫含む）への
 * 畳み込みは FE 側（FileBrowser のツリー構築）で行う。
 */
export interface LibraryFolderStats {
  /** 対象フォルダ（null = セクションルート直下） */
  folder_id: string | null;
  file_count: number;
  /** 直下ファイルの合計バイト数（サイズ不明のみなら null） */
  total_size: number | null;
  /** 直下ファイルの最終更新日時 */
  latest_file_at: string | null;
}

export interface LibrarySection {
  id: string;
  title: string;
  sort_order: number;
  my_role: LibraryRole;
  /** ファイル/フォルダ/共有リンクの編集ができるか（エディター以上） */
  can_edit: boolean;
  /** ロールの変更ができるか（オーナー / master_admin） */
  can_manage_roles: boolean;
  /** 対象プロジェクトが 1 件のときそのバナーカラー。それ以外は null */
  banner_color: string | null;
  folders: LibraryFolderItem[];
  /**
   * フォルダ直下のファイル集計。ファイル本体はツリー応答に含まれない
   * （getLibrarySectionFiles でフォルダ単位に遅延取得する）。
   */
  folder_stats: LibraryFolderStats[];
  settings?: LibrarySectionSettings;
}

export interface LibraryTreeResponse {
  status: string;
  data: {
    sections: LibrarySection[];
    /** セクションの作成・権限設定変更ができるか（master_admin のみ true） */
    can_manage: boolean;
  };
}

export interface LibrarySectionResponse {
  status: string;
  data: { section: LibrarySection };
}

export interface LibraryFolderResponse {
  status: string;
  data: { folder: LibraryFolderItem };
}

export interface LibraryFileResponse {
  status: string;
  data: { file: LibraryFileItem };
}

/** GET /api/library/sections/<id>/files の応答 */
export interface LibrarySectionFilesResponse {
  status: string;
  data: {
    section_id: string;
    /** 取得対象フォルダ（null = セクションルート直下） */
    folder_id: string | null;
    recursive: boolean;
    query: string | null;
    files: LibraryFileItem[];
  };
}

/**
 * getLibrarySectionFiles の蓄積キャッシュ形。
 * セクション単位で 1 エントリに、フォルダごとの取得結果を merge で
 * 蓄積する（無限スクロールと同じ serializeQueryArgs + merge パターン）。
 */
export interface LibrarySectionFilesCache {
  files: LibraryFileItem[];
  /** ロード済みフォルダキー（folderCacheKey 形式。"root" = ルート直下） */
  loadedFolderKeys: string[];
}

/** 共有リンク（GET でリンクが無ければ data: null） */
export interface LibraryShareLink {
  id: string;
  token: string;
  expires_at: string | null;
  require_membership: boolean;
}

export interface LibraryShareLinkResponse {
  status: string;
  data: LibraryShareLink | null;
}

/** オーディオプレビューのブックマーク（チーム共有。点 or 範囲） */
export interface LibraryBookmark {
  id: string;
  start_seconds: number;
  /** null = 点のブックマーク */
  end_seconds: number | null;
  label: string;
  /** 「ブックマーク」ページ上の所属フォルダ（null = 作成者ツリー直下） */
  folder_id: string | null;
  created_by: LibraryUserBrief | null;
  created_at: string;
  updated_at: string;
}

export interface LibraryBookmarksResponse {
  status: string;
  data: { bookmarks: LibraryBookmark[] };
}

export interface LibraryBookmarkResponse {
  status: string;
  data: { bookmark: LibraryBookmark };
}

/** セクション横断一覧の 1 件（「ブックマーク」ページ用。対象ファイル付き） */
export interface LibrarySectionBookmark extends LibraryBookmark {
  file: {
    id: string;
    name: string;
    file_type: LibraryFileType;
    /** 所属フォルダ（null = ルート直下） */
    folder_id: string | null;
  };
}

/** 「ブックマーク」ページの整理フォルダ（作成者ユーザーごとのツリー） */
export interface LibraryBookmarkFolderItem {
  id: string;
  name: string;
  /** 親フォルダ（null = ツリー直下） */
  parent_id: string | null;
  owner: LibraryUserBrief | null;
  created_at: string;
  updated_at: string;
}

export interface LibrarySectionBookmarksResponse {
  status: string;
  data: {
    bookmarks: LibrarySectionBookmark[];
    folders: LibraryBookmarkFolderItem[];
    /**
     * ブックマーク対象ファイルのフル情報（試聴用 URL / 波形付き）。
     * ツリー API がファイル本体を返さないため、ページ内の再生は
     * この一覧で自己完結する。
     */
    files: LibraryFileItem[];
  };
}

export interface LibraryBookmarkFolderResponse {
  status: string;
  data: { folder: LibraryBookmarkFolderItem };
}

// ── リクエストパラメータ ──────────────────────────────────────────────

export interface GetLibraryTreeParams {
  /**
   * 指定すると、セクション設定でそのプロジェクトを対象にした
   * セクションのみ返す（プロジェクト内の下部バー・library タブ用）。
   */
  project_id?: string;
}

export interface GetLibrarySectionFilesParams {
  section_id: string;
  /** 取得するフォルダ（null = セクションルート直下） */
  folder_id: string | null;
}

export interface SearchLibraryFilesParams {
  section_id: string;
  /** 検索スコープ（null = セクション全体。配下を再帰検索する） */
  folder_id: string | null;
  q: string;
}

/** 蓄積キャッシュ内でセクションルート直下を表すフォルダキー */
export const LIBRARY_ROOT_FOLDER_KEY = "root";

/** フォルダ ID を蓄積キャッシュのキーへ変換（null = ルート直下） */
export const libraryFolderCacheKey = (folderId: string | null): string =>
  folderId ?? LIBRARY_ROOT_FOLDER_KEY;

/** ロール設定の送信形（position / user_id はどちらか一方必須） */
export interface LibraryRoleInput {
  role: LibraryRoleName;
  position?: LibraryPosition;
  user_id?: number;
}

export interface LibrarySectionSettingsParams {
  title?: string;
  sort_order?: number;
  /** 対象プロジェクト（1 件以上必須） */
  project_ids?: string[];
  allowed_positions?: LibraryPosition[];
  allow_completed?: boolean;
  /** ロール設定（全置換） */
  roles?: LibraryRoleInput[];
}

export interface UpdateLibrarySectionParams
  extends LibrarySectionSettingsParams {
  section_id: string;
}

export interface CreateLibraryFolderParams {
  section_id: string;
  name: string;
  /** 親フォルダ（省略 = ルート直下） */
  parent_id?: string;
}

export interface UpdateLibraryFolderParams {
  folder_id: string;
  /** リネーム */
  name?: string;
  /** 移動先（null = ルート直下）。自分自身・子孫への移動は 400 */
  parent_id?: string | null;
}

export interface RegisterLibraryFileParams {
  section_id: string;
  /** purpose=shared_library でアップロード済みの S3 キー */
  file_key: string;
  /** 追加先フォルダ（省略 = ルート直下） */
  folder_id?: string;
  /** folder_id（またはルート）を起点に階層作成するパス（OS フォルダ D&D 用） */
  folder_path?: string;
  /**
   * ツリーの再取得を呼び出し側に任せる（リクエストには送らない）。
   *
   * 複数ファイルを連続登録するとき、1 件ごとにツリー全体を取り直すと
   * 件数が少しずつ増える表示になり、完了までの時間も伸びる。まとめて
   * 登録する場合は true にして、最後に 1 回だけ invalidate すること。
   */
  defer_invalidate?: boolean;
}

export interface UpdateLibraryFileParams {
  file_id: string;
  /** リネーム（表示名） */
  file_name?: string;
  /** 移動先フォルダ（null = ルート直下） */
  folder_id?: string | null;
  /** 差し替え（アップロード済みの新しい S3 キー） */
  file_key?: string;
}

export interface CreateLibraryBookmarkParams {
  file_id: string;
  start_seconds: number;
  /** 省略 / null = 点のブックマーク */
  end_seconds?: number | null;
  label?: string;
}

export interface UpdateLibraryBookmarkParams {
  bookmark_id: string;
  /** キャッシュタグ（LibraryBookmark, id=file_id）の解決に使う */
  file_id: string;
  label?: string;
  start_seconds?: number;
  /** null を渡すと点のブックマークへ変換 */
  end_seconds?: number | null;
  /**
   * 「ブックマーク」ページ上の移動先（null = 作成者ツリー直下）。
   * 移動先はブックマーク作成者がオーナーのフォルダのみ。
   */
  folder_id?: string | null;
}

export interface CreateLibraryBookmarkFolderParams {
  section_id: string;
  name: string;
  /** 親フォルダ（省略 = ツリー直下。自分のフォルダのみ） */
  parent_id?: string;
}

export interface UpdateLibraryBookmarkFolderParams {
  folder_id: string;
  /** リネーム */
  name?: string;
  /** 移動先（null = ツリー直下）。同じオーナーのフォルダのみ */
  parent_id?: string | null;
}

/** 共有対象の指定（file_id / folder_id はどちらか一方必須） */
export interface LibraryShareTargetParams {
  file_id?: string;
  folder_id?: string;
}

export interface CreateLibraryShareLinkParams
  extends LibraryShareTargetParams {
  /** 有効期限（ISO 日時 or YYYY-MM-DD。null/省略 = 無期限） */
  expires_at?: string | null;
  /** セクションの対象プロジェクトのメンバーのみに制限（既定 true） */
  require_membership?: boolean;
}

const shareTargetQuery = (params: LibraryShareTargetParams): string => {
  const search = new URLSearchParams();
  if (params.file_id) search.set("file_id", params.file_id);
  if (params.folder_id) search.set("folder_id", params.folder_id);
  return search.toString();
};

// ── キャッシュの直接更新 ──────────────────────────────────────────────
//
// フォルダ / ファイルの単体操作は、成功応答（サーバー正データ）を
// getLibraryTree（フォルダ + 集計）と getLibrarySectionFiles（蓄積
// ファイル）のキャッシュへ直接反映する（meSchedule / credits と同じ
// updateQueryData パターン）。膨大なライブラリで操作のたびに再取得
// すると、反映まで待たされ「作成したのに出てこない」状態になるため、
// invalidatesTags による再取得は集計のずれ補正（TREE）以外行わない。

type LibraryTreeRecipe = (draft: LibraryTreeResponse) => void;

/**
 * getLibraryTree の全キャッシュバリアント（全件 / project 絞り込み）へ
 * 同じ変更を適用する。対象セクションを含まないバリアントでは
 * recipe 側が no-op になるように書くこと。
 */
const patchLibraryTreeCaches = (
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  recipe: LibraryTreeRecipe,
): void => {
  const cachedArgs = libraryApi.util.selectCachedArgsForQuery(
    getState() as Parameters<
      typeof libraryApi.util.selectCachedArgsForQuery
    >[0],
    "getLibraryTree",
  );
  cachedArgs.forEach((arg) => {
    dispatch(libraryApi.util.updateQueryData("getLibraryTree", arg, recipe));
  });
};

type LibrarySectionFilesRecipe = (draft: LibrarySectionFilesCache) => void;

/**
 * getLibrarySectionFiles の全キャッシュエントリ（セクション単位）へ
 * 同じ変更を適用する。対象ファイルを含まないセクションでは recipe 側が
 * no-op になるように書くこと。onlySectionId を渡すと、そのセクションの
 * キャッシュにだけ適用する（ファイル追加など no-op にできない変更用）。
 */
const patchLibrarySectionFilesCaches = (
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  recipe: LibrarySectionFilesRecipe,
  onlySectionId?: string,
): void => {
  const cachedArgs = libraryApi.util.selectCachedArgsForQuery(
    getState() as Parameters<
      typeof libraryApi.util.selectCachedArgsForQuery
    >[0],
    "getLibrarySectionFiles",
  );
  cachedArgs.forEach((arg) => {
    if (onlySectionId && arg.section_id !== onlySectionId) return;
    dispatch(
      libraryApi.util.updateQueryData("getLibrarySectionFiles", arg, recipe),
    );
  });
};

/** フォルダ ID を含むセクションをドラフトから引く（無ければ undefined） */
const findSectionByFolder = (
  draft: LibraryTreeResponse,
  folderId: string,
): LibrarySection | undefined =>
  draft.data.sections.find((s) => s.folders.some((f) => f.id === folderId));

/**
 * フォルダ自身と子孫の ID 集合を返す（削除の波及・スコープ絞り込み用。
 * ライブラリ / ブックマークどちらのフォルダにも使える）。
 */
export const collectDescendantFolderIds = (
  folders: { id: string; parent_id: string | null }[],
  rootId: string,
): Set<string> => {
  const byParent = new Map<string | null, string[]>();
  folders.forEach((f) => {
    const list = byParent.get(f.parent_id) ?? [];
    list.push(f.id);
    byParent.set(f.parent_id, list);
  });
  const ids = new Set<string>([rootId]);
  const walk = (id: string): void => {
    (byParent.get(id) ?? []).forEach((child) => {
      ids.add(child);
      walk(child);
    });
  };
  walk(rootId);
  return ids;
};

export const libraryApi = createApi({
  reducerPath: "libraryApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["LibraryTree", "LibraryShareLink", "LibraryBookmark"],
  endpoints: (builder) => ({
    getLibraryTree: builder.query<
      LibraryTreeResponse,
      GetLibraryTreeParams | void
    >({
      query: (params) =>
        params?.project_id
          ? `/api/library?project=${encodeURIComponent(params.project_id)}`
          : "/api/library",
      // 全件/プロジェクト絞り込みのどちらのキャッシュも同一タグにし、
      // ファイル/フォルダ更新時の invalidate が全バリアントへ波及するようにする。
      providesTags: [{ type: "LibraryTree", id: "TREE" }],
      // 画面を離れても 5 分はキャッシュを保持してタブ往復での再取得を
      // 避ける（単体操作は patchLibraryTreeCaches で直接反映される）。
      keepUnusedDataFor: 300,
    }),

    /**
     * セクション内ファイルのフォルダ単位取得（ツリーの遅延ロード）。
     *
     * セクション単位で 1 つの蓄積キャッシュへ merge する
     * （無限スクロールと同じ serializeQueryArgs + merge パターン）:
     * - serializeQueryArgs: section_id のみでキー化
     * - merge: 取得フォルダの旧ファイルを差し替えつつ union
     * - forceRefetch: 未ロードのフォルダへ arg が変わったときだけ再取得
     *
     * 呼び出し側（Library.tsx）は「スコープ + 展開中フォルダ」を順に
     * arg として流し、蓄積された全ファイルを FileBrowser へ渡す。
     * 同一キャッシュキーの in-flight 中は新しい arg の fetch がスキップ
     * されるため、呼び出し側でロード完了を待って順に要求すること。
     */
    getLibrarySectionFiles: builder.query<
      LibrarySectionFilesCache,
      GetLibrarySectionFilesParams
    >({
      query: ({ section_id, folder_id }) =>
        folder_id
          ? `/api/library/sections/${section_id}/files?folder=${folder_id}`
          : `/api/library/sections/${section_id}/files`,
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.section_id})`,
      transformResponse: (resp: LibrarySectionFilesResponse) => ({
        files: resp.data.files,
        loadedFolderKeys: [libraryFolderCacheKey(resp.data.folder_id)],
      }),
      merge: (current, incoming) => {
        const key = incoming.loadedFolderKeys[0];
        const incomingIds = new Set(incoming.files.map((f) => f.id));
        // 再取得したフォルダの旧ファイルを除いてから新しい結果を足す
        current.files = current.files.filter(
          (f) =>
            libraryFolderCacheKey(f.folder_id) !== key &&
            !incomingIds.has(f.id),
        );
        current.files.push(...incoming.files);
        if (!current.loadedFolderKeys.includes(key)) {
          current.loadedFolderKeys.push(key);
        }
      },
      forceRefetch: ({ currentArg, previousArg, endpointState }) => {
        if (!currentArg) return false;
        if (currentArg.folder_id === previousArg?.folder_id) return false;
        const data = endpointState?.data as
          | LibrarySectionFilesCache
          | undefined;
        return !data?.loadedFolderKeys.includes(
          libraryFolderCacheKey(currentArg.folder_id),
        );
      },
      // セクションを離れても 5 分は蓄積を保持する（タブ往復対策）。
      keepUnusedDataFor: 300,
    }),

    /** セクション内ファイルのサーバー検索（スコープ配下を再帰・部分一致） */
    searchLibraryFiles: builder.query<
      LibraryFileItem[],
      SearchLibraryFilesParams
    >({
      query: ({ section_id, folder_id, q }) => {
        const search = new URLSearchParams();
        if (folder_id) search.set("folder", folder_id);
        search.set("q", q);
        return `/api/library/sections/${section_id}/files?${search.toString()}`;
      },
      transformResponse: (resp: LibrarySectionFilesResponse) =>
        resp.data.files,
      // 検索結果は短命で良い（編集操作の反映は再検索に任せる）
      keepUnusedDataFor: 60,
    }),

    /**
     * フォルダ配下（子孫含む）の全ファイル取得（zip 一括ダウンロード用）。
     * キャッシュには残さない（利用時に initiate → unwrap で使い切る）。
     */
    getLibraryFolderFilesRecursive: builder.query<
      LibraryFileItem[],
      { section_id: string; folder_id: string | null }
    >({
      query: ({ section_id, folder_id }) => {
        const search = new URLSearchParams();
        if (folder_id) search.set("folder", folder_id);
        search.set("recursive", "true");
        return `/api/library/sections/${section_id}/files?${search.toString()}`;
      },
      transformResponse: (resp: LibrarySectionFilesResponse) =>
        resp.data.files,
      keepUnusedDataFor: 0,
    }),

    // ── セクション ────────────────────────────────────────────────

    createLibrarySection: builder.mutation<
      LibrarySectionResponse,
      LibrarySectionSettingsParams & { title: string }
    >({
      query: (body) => ({
        url: "/api/library/sections",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LibraryTree", id: "TREE" }],
    }),

    updateLibrarySection: builder.mutation<
      LibrarySectionResponse,
      UpdateLibrarySectionParams
    >({
      query: ({ section_id, ...body }) => ({
        url: `/api/library/sections/${section_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "LibraryTree", id: "TREE" }],
    }),

    deleteLibrarySection: builder.mutation<
      { status: string; message: string },
      { section_id: string }
    >({
      query: ({ section_id }) => ({
        url: `/api/library/sections/${section_id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "LibraryTree", id: "TREE" }],
    }),

    /** ロール割り当て候補ユーザーの検索（オーナー / master_admin） */
    getLibraryRoleCandidates: builder.query<
      {
        status: string;
        data: {
          users: (LibraryUserBrief & { positions: LibraryPosition[] })[];
        };
      },
      { section_id: string; q: string }
    >({
      query: ({ section_id, q }) =>
        `/api/library/sections/${section_id}/role-candidates?q=${encodeURIComponent(q)}`,
    }),

    // ── フォルダ ──────────────────────────────────────────────────

    createLibraryFolder: builder.mutation<
      LibraryFolderResponse,
      CreateLibraryFolderParams
    >({
      query: (body) => ({
        url: "/api/library/folders",
        method: "POST",
        body,
      }),
      // 応答の folder（サーバー正データ）をキャッシュへ即時追加する。
      // ツリー全体の再取得はしない（ファイル先頭のコメント参照）。
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const created = data.data.folder;
          patchLibraryTreeCaches(dispatch, getState, (draft) => {
            const section = draft.data.sections.find(
              (s) => s.id === arg.section_id,
            );
            if (!section) return;
            if (section.folders.some((f) => f.id === created.id)) return;
            section.folders.push(created);
          });
        } catch {
          /* noop: エラーは呼び出し元のトーストで表示 */
        }
      },
    }),

    updateLibraryFolder: builder.mutation<
      LibraryFolderResponse,
      UpdateLibraryFolderParams
    >({
      query: ({ folder_id, ...body }) => ({
        url: `/api/library/folders/${folder_id}`,
        method: "PATCH",
        body,
      }),
      // リネーム / 移動の結果をキャッシュ内の該当フォルダへ差し替える。
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const updated = data.data.folder;
          patchLibraryTreeCaches(dispatch, getState, (draft) => {
            const section = findSectionByFolder(draft, arg.folder_id);
            if (!section) return;
            const index = section.folders.findIndex(
              (f) => f.id === arg.folder_id,
            );
            if (index >= 0) section.folders[index] = updated;
          });
        } catch {
          /* noop */
        }
      },
    }),

    /** フォルダを中身（子孫フォルダ・ファイル）ごと削除する */
    deleteLibraryFolder: builder.mutation<
      { status: string; data: { deleted_folder_id: string } },
      { folder_id: string }
    >({
      query: ({ folder_id }) => ({
        url: `/api/library/folders/${folder_id}`,
        method: "DELETE",
      }),
      // BE は CASCADE で中身ごと消すため、キャッシュ側も子孫フォルダと
      // その集計・蓄積済みの配下ファイルをまとめて除去する。
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          // 対象セクションを含むツリーのバリアントで削除対象が確定する
          const removed = new Set<string>();
          patchLibraryTreeCaches(dispatch, getState, (draft) => {
            const section = findSectionByFolder(draft, arg.folder_id);
            if (!section) return;
            collectDescendantFolderIds(section.folders, arg.folder_id).forEach(
              (id) => removed.add(id),
            );
            section.folders = section.folders.filter(
              (f) => !removed.has(f.id),
            );
            section.folder_stats = section.folder_stats.filter(
              (s) => s.folder_id === null || !removed.has(s.folder_id),
            );
          });
          if (removed.size > 0) {
            patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
              draft.files = draft.files.filter(
                (f) => f.folder_id === null || !removed.has(f.folder_id),
              );
              draft.loadedFolderKeys = draft.loadedFolderKeys.filter(
                (key) => !removed.has(key),
              );
            });
          }
        } catch {
          /* noop */
        }
      },
    }),

    // ── ファイル ──────────────────────────────────────────────────

    /** S3 アップロード済みファイルのメタデータ登録（upload/register の代替） */
    registerLibraryFile: builder.mutation<
      LibraryFileResponse,
      RegisterLibraryFileParams
    >({
      query: ({ defer_invalidate: _deferInvalidate, ...body }) => ({
        url: "/api/library/files",
        method: "POST",
        body,
      }),
      // ツリー（フォルダ + 集計）は invalidate で取り直す（軽量応答）。
      // folder_path で新フォルダが作られることがあるためツリー再取得は必須。
      invalidatesTags: (_result, _error, arg) =>
        arg.defer_invalidate ? [] : [{ type: "LibraryTree", id: "TREE" }],
      // 蓄積ファイルキャッシュへは応答の file を即時追加する
      // （defer_invalidate のバッチ登録でも 1 件ずつ一覧に現れる）。
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const created = data.data.file;
          patchLibrarySectionFilesCaches(
            dispatch,
            getState,
            (draft) => {
              if (draft.files.some((f) => f.id === created.id)) return;
              draft.files.push(created);
            },
            arg.section_id,
          );
        } catch {
          /* noop */
        }
      },
    }),

    updateLibraryFile: builder.mutation<
      LibraryFileResponse,
      UpdateLibraryFileParams
    >({
      query: ({ file_id, ...body }) => ({
        url: `/api/library/files/${file_id}`,
        method: "PATCH",
        body,
      }),
      // フォルダ移動・差し替えは集計（件数 / サイズ / 最終更新）が変わる
      // ため、軽量なツリーのみ invalidate で取り直す。
      invalidatesTags: (_result, _error, arg) =>
        "folder_id" in arg || arg.file_key
          ? [{ type: "LibraryTree", id: "TREE" }]
          : [],
      // リネーム / 移動 / 差し替えの結果をキャッシュ内の該当ファイルへ
      // 差し替える（応答は URL 類も含む完全なファイル情報）。
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const updated = data.data.file;
          patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
            const index = draft.files.findIndex((f) => f.id === arg.file_id);
            if (index >= 0) draft.files[index] = updated;
          });
        } catch {
          /* noop */
        }
      },
    }),

    deleteLibraryFile: builder.mutation<
      { status: string; data: { deleted_file_id: string } },
      { file_id: string }
    >({
      query: ({ file_id }) => ({
        url: `/api/library/files/${file_id}`,
        method: "DELETE",
      }),
      // 集計の更新のため軽量なツリーのみ invalidate で取り直す。
      invalidatesTags: [{ type: "LibraryTree", id: "TREE" }],
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
            draft.files = draft.files.filter((f) => f.id !== arg.file_id);
          });
        } catch {
          /* noop */
        }
      },
    }),

    // ── オーディオブックマーク ────────────────────────────────────
    //
    // タグは LibraryBookmark のみ（id = file_id と、セクション横断一覧用の
    // "SECTION-LIST"）。LibraryTree タグは invalidate しない（ブックマーク
    // 1 つでツリー全体の再取得が走り、初期表示の重さを持ち込むため）。
    // ファイル一覧のアバター表示用の bookmark_users / bookmark_count だけは
    // patchLibraryTreeCaches でキャッシュへ直接反映する。

    getLibraryFileBookmarks: builder.query<
      LibraryBookmarksResponse,
      { file_id: string }
    >({
      query: ({ file_id }) => `/api/library/files/${file_id}/bookmarks`,
      providesTags: (_result, _error, arg) => [
        { type: "LibraryBookmark", id: arg.file_id },
      ],
    }),

    /** セクション内の全ブックマーク（サイドバーの「ブックマーク」ページ用） */
    getLibrarySectionBookmarks: builder.query<
      LibrarySectionBookmarksResponse,
      { section_id: string }
    >({
      query: ({ section_id }) =>
        `/api/library/sections/${section_id}/bookmarks`,
      providesTags: [{ type: "LibraryBookmark", id: "SECTION-LIST" }],
    }),

    createLibraryBookmark: builder.mutation<
      LibraryBookmarkResponse,
      CreateLibraryBookmarkParams
    >({
      query: ({ file_id, ...body }) => ({
        url: `/api/library/files/${file_id}/bookmarks`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "LibraryBookmark", id: arg.file_id },
        { type: "LibraryBookmark", id: "SECTION-LIST" },
      ],
      // ファイル一覧のアバター表示用に件数と作成者を直接反映する。
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const createdBy = data.data.bookmark.created_by;
          patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
            const file = draft.files.find((f) => f.id === arg.file_id);
            if (!file) return;
            file.bookmark_count += 1;
            if (
              createdBy &&
              !file.bookmark_users.some((u) => u.id === createdBy.id)
            ) {
              file.bookmark_users.push(createdBy);
            }
          });
        } catch {
          /* noop */
        }
      },
    }),

    updateLibraryBookmark: builder.mutation<
      LibraryBookmarkResponse,
      UpdateLibraryBookmarkParams
    >({
      query: ({ bookmark_id, file_id: _fileId, ...body }) => ({
        url: `/api/library/bookmarks/${bookmark_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "LibraryBookmark", id: arg.file_id },
        { type: "LibraryBookmark", id: "SECTION-LIST" },
      ],
    }),

    deleteLibraryBookmark: builder.mutation<
      { status: string; data: { deleted_bookmark_id: string } },
      { bookmark_id: string; file_id: string }
    >({
      query: ({ bookmark_id }) => ({
        url: `/api/library/bookmarks/${bookmark_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "LibraryBookmark", id: arg.file_id },
        { type: "LibraryBookmark", id: "SECTION-LIST" },
      ],
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          // 削除後の作成者一覧はファイル単位のブックマークキャッシュから
          // 再計算する（同じ作成者の別ブックマークが残っていることがある）。
          // キャッシュが無ければ件数のみ減算し、0 件になったら空にする。
          const state = getState() as Parameters<
            typeof libraryApi.util.selectCachedArgsForQuery
          >[0];
          const fileBookmarksCache = libraryApi.endpoints
            .getLibraryFileBookmarks.select({ file_id: arg.file_id })(
              state as never,
            ).data;
          const remaining = fileBookmarksCache?.data.bookmarks.filter(
            (b) => b.id !== arg.bookmark_id,
          );
          patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
            const file = draft.files.find((f) => f.id === arg.file_id);
            if (!file) return;
            file.bookmark_count = Math.max(0, file.bookmark_count - 1);
            if (remaining) {
              const users: LibraryUserBrief[] = [];
              remaining.forEach((b) => {
                if (
                  b.created_by &&
                  !users.some((u) => u.id === b.created_by!.id)
                ) {
                  users.push(b.created_by);
                }
              });
              file.bookmark_users = users;
            } else if (file.bookmark_count === 0) {
              file.bookmark_users = [];
            }
          });
        } catch {
          /* noop */
        }
      },
    }),

    // ── ブックマークの整理フォルダ（「ブックマーク」ページ） ─────────

    createLibraryBookmarkFolder: builder.mutation<
      LibraryBookmarkFolderResponse,
      CreateLibraryBookmarkFolderParams
    >({
      query: ({ section_id, ...body }) => ({
        url: `/api/library/sections/${section_id}/bookmark-folders`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LibraryBookmark", id: "SECTION-LIST" }],
    }),

    updateLibraryBookmarkFolder: builder.mutation<
      LibraryBookmarkFolderResponse,
      UpdateLibraryBookmarkFolderParams
    >({
      query: ({ folder_id, ...body }) => ({
        url: `/api/library/bookmark-folders/${folder_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "LibraryBookmark", id: "SECTION-LIST" }],
    }),

    deleteLibraryBookmarkFolder: builder.mutation<
      { status: string; data: { deleted_folder_id: string } },
      { folder_id: string }
    >({
      query: ({ folder_id }) => ({
        url: `/api/library/bookmark-folders/${folder_id}`,
        method: "DELETE",
      }),
      // フォルダ削除は中のブックマークごと消えるため、ファイル単位の
      // キャッシュ（インスペクタ表示）とセクション一覧を作り直す。
      invalidatesTags: [
        { type: "LibraryBookmark", id: "SECTION-LIST" },
        "LibraryBookmark",
      ],
      // ファイル一覧のアバター（bookmark_count / bookmark_users）は、
      // 削除前のセクション一覧キャッシュから「フォルダ配下の消える
      // ブックマーク」と「残存ブックマークの作成者」を求めて反映する。
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const state = getState() as Parameters<
          typeof libraryApi.util.selectCachedArgsForQuery
        >[0];
        const removedCounts = new Map<string, number>();
        const remainingUsers = new Map<string, LibraryUserBrief[]>();
        let found = false;
        for (const listArg of libraryApi.util.selectCachedArgsForQuery(
          state,
          "getLibrarySectionBookmarks",
        )) {
          const cache = libraryApi.endpoints.getLibrarySectionBookmarks
            .select(listArg)(state as never).data;
          if (!cache?.data.folders.some((f) => f.id === arg.folder_id)) {
            continue;
          }
          found = true;
          const removedFolderIds = collectDescendantFolderIds(
            cache.data.folders,
            arg.folder_id,
          );
          cache.data.bookmarks.forEach((b) => {
            if (b.folder_id !== null && removedFolderIds.has(b.folder_id)) {
              removedCounts.set(
                b.file.id,
                (removedCounts.get(b.file.id) ?? 0) + 1,
              );
            } else if (b.created_by) {
              let users = remainingUsers.get(b.file.id);
              if (!users) {
                users = [];
                remainingUsers.set(b.file.id, users);
              }
              if (!users.some((u) => u.id === b.created_by!.id)) {
                users.push(b.created_by);
              }
            }
          });
          break;
        }
        try {
          await queryFulfilled;
          if (!found) return;
          patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
            removedCounts.forEach((count, fileId) => {
              const file = draft.files.find((f) => f.id === fileId);
              if (!file) return;
              file.bookmark_count = Math.max(
                0,
                file.bookmark_count - count,
              );
              file.bookmark_users = remainingUsers.get(fileId) ?? [];
            });
          });
        } catch {
          /* noop */
        }
      },
    }),

    // ── 共有リンク ────────────────────────────────────────────────

    getLibraryShareLink: builder.query<
      LibraryShareLinkResponse,
      LibraryShareTargetParams
    >({
      query: (params) => `/api/library/share_links?${shareTargetQuery(params)}`,
      providesTags: (_result, _error, arg) => [
        {
          type: "LibraryShareLink",
          id: arg.file_id ?? arg.folder_id ?? "UNKNOWN",
        },
      ],
    }),

    createLibraryShareLink: builder.mutation<
      { status: string; data: LibraryShareLink },
      CreateLibraryShareLinkParams
    >({
      query: (body) => ({
        url: "/api/library/share_links",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: "LibraryShareLink",
          id: arg.file_id ?? arg.folder_id ?? "UNKNOWN",
        },
        // フォルダの is_shared バッジはツリー側にあるため再取得する。
        // ファイルの is_shared は下の onQueryStarted で直接反映する。
        ...(arg.folder_id
          ? [{ type: "LibraryTree" as const, id: "TREE" }]
          : []),
      ],
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        if (!arg.file_id) return;
        try {
          await queryFulfilled;
          patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
            const file = draft.files.find((f) => f.id === arg.file_id);
            if (file) file.is_shared = true;
          });
        } catch {
          /* noop */
        }
      },
    }),

    deleteLibraryShareLink: builder.mutation<
      { status: string; data: null },
      LibraryShareTargetParams
    >({
      query: (params) => ({
        url: `/api/library/share_links?${shareTargetQuery(params)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: "LibraryShareLink",
          id: arg.file_id ?? arg.folder_id ?? "UNKNOWN",
        },
        ...(arg.folder_id
          ? [{ type: "LibraryTree" as const, id: "TREE" }]
          : []),
      ],
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        if (!arg.file_id) return;
        try {
          await queryFulfilled;
          patchLibrarySectionFilesCaches(dispatch, getState, (draft) => {
            const file = draft.files.find((f) => f.id === arg.file_id);
            if (file) file.is_shared = false;
          });
        } catch {
          /* noop */
        }
      },
    }),
  }),
});

export const {
  useGetLibraryTreeQuery,
  useGetLibrarySectionFilesQuery,
  useLazyGetLibrarySectionFilesQuery,
  useSearchLibraryFilesQuery,
  useLazyGetLibraryFolderFilesRecursiveQuery,
  useCreateLibrarySectionMutation,
  useUpdateLibrarySectionMutation,
  useDeleteLibrarySectionMutation,
  useGetLibraryRoleCandidatesQuery,
  useCreateLibraryFolderMutation,
  useUpdateLibraryFolderMutation,
  useDeleteLibraryFolderMutation,
  useRegisterLibraryFileMutation,
  useUpdateLibraryFileMutation,
  useDeleteLibraryFileMutation,
  useGetLibraryFileBookmarksQuery,
  useGetLibrarySectionBookmarksQuery,
  useCreateLibraryBookmarkMutation,
  useUpdateLibraryBookmarkMutation,
  useDeleteLibraryBookmarkMutation,
  useCreateLibraryBookmarkFolderMutation,
  useUpdateLibraryBookmarkFolderMutation,
  useDeleteLibraryBookmarkFolderMutation,
  useLazyGetLibraryShareLinkQuery,
  useGetLibraryShareLinkQuery,
  useCreateLibraryShareLinkMutation,
  useDeleteLibraryShareLinkMutation,
} = libraryApi;
